import { faToggleOn, faToggleOff, faRefresh, faUser, faUserCircle, faBookOpen, faClock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React from 'react'
import { Card, Row, Col } from 'react-bootstrap';
import StatsCard from '../../component/Cards/StatsCard';
import StatsCardWithProgress from '../../component/Cards/StatsCardWithProgress';
import DonutChart, { ChartDataItem } from '../../component/Charts/DonutChart';
import { NoDataView } from './VenueWiseReport';

interface AttendanceOverViewProps {
    data: any;
    loading: boolean;
    chartData: ChartDataItem[];
    autoRefresh: boolean;
    setAutoRefresh: (value: boolean) => void;
    getData: () => void;
    setNextRefreshIn: (seconds: number) => void;
    lastSynced: any;
    nextRefreshIn: any;
}

export default function AttendanceOverview({ data, loading, chartData, autoRefresh, setAutoRefresh, getData, setNextRefreshIn, lastSynced, nextRefreshIn }: AttendanceOverViewProps) {
    return (
        <>
            <Card
                className="mt-4 shadow"
                style={{
                    borderRadius: "16px",
                    border: "1px solid #e9ecef",
                }}
            >
                <Card.Body className="p-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h5 className="text-secondary fw-bold mb-0" style={{ fontSize: '18px' }}>Attendance Overview</h5>
                        <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={autoRefresh ? faToggleOn : faToggleOff}
                                    className={`cursor-pointer me-2 ${autoRefresh ? 'text-success' : 'text-muted'}`}
                                    style={{ fontSize: '18px' }}
                                    onClick={() => {
                                        setAutoRefresh(!autoRefresh);
                                        if (!autoRefresh) {
                                            setNextRefreshIn(120);
                                        }
                                    }}
                                    title={`Auto-refresh ${autoRefresh ? 'enabled' : 'disabled'}`}
                                />
                                <small className="text-muted">Auto-refresh</small>
                            </div>

                            <FontAwesomeIcon
                                icon={faRefresh}
                                className={`cursor-pointer text-primary ${loading ? 'fa-spin' : ''}`}
                                onClick={() => {
                                    getData();
                                    if (autoRefresh) {
                                        setNextRefreshIn(120);
                                    }
                                }}
                                style={{ fontSize: '16px' }}
                                title="Refresh now"
                            />
                        </div>
                    </div>

                    <Row>
                        <Col md={6} className="">
                            {chartData.length > 0 && data ? (
                                <DonutChart
                                    data={chartData}
                                    centerText={{
                                        label: "Total candidates",
                                        value: data?.total_count || 0
                                    }}
                                    height={400}
                                    width={400}
                                    showTooltip={true}
                                    showLegend={true}
                                    showCalloutLabels={true}
                                />
                            ) : (
                                <NoDataView message="No attendance data available for the selected shift." />
                            )}
                        </Col>
                        <Col md={6} className="">
                            {
                                data ?
                                    (
                                        <Col>
                                            <Col md={12} className="mb-3">
                                                <StatsCard
                                                    title="Candidates"
                                                    value={data?.total_count || 0}
                                                    icon={faUser}
                                                    iconColor="primary"
                                                    valueColor="primary"
                                                />
                                            </Col>
                                            <Col md={12} className="mb-3">
                                                <StatsCard
                                                    title="Present Candidates"
                                                    value={data?.present_count || 0}
                                                    synced_count={data?.synced_count || 0}
                                                    icon={faUserCircle}
                                                    iconColor="success"
                                                    valueColor="success"
                                                />
                                            </Col>
                                            <Col md={12} className="mb-3">
                                                <StatsCardWithProgress
                                                    title="Attendance Rate"
                                                    value={`${((data?.present_count || 0) / (data?.total_count || 1) * 100).toFixed(2)}%`}
                                                    progress={(data?.present_count || 0) / (data?.total_count || 1) * 100}
                                                    icon={faBookOpen}
                                                    iconColor="success"
                                                    valueColor="success"
                                                />
                                            </Col>
                                        </Col>
                                    )
                                    : <NoDataView message="No attendance data available for the selected shift." />
                            }
                        </Col>
                    </Row>

                    {lastSynced && (
                        <div className="mt-1 d-flex align-items-center justify-content-end">
                            <FontAwesomeIcon icon={faClock} className="text-muted me-2" style={{ fontSize: '12px' }} />
                            <small className="text-muted">
                                Last synced: {moment(lastSynced).format('DD MMM YYYY, hh:mm:ss')}
                                {autoRefresh && (
                                    <span className="ms-2">
                                        • Next refresh in: <span className="fw-bold text-primary">{formatCountdown(nextRefreshIn)}</span>
                                    </span>
                                )}
                            </small>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </>
    )
}

const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};